import { useMemo } from 'react';
import { Asset, EpisodeAsset, MovieAsset, ProgramAsset, SeriesAsset } from '../../queries/ats/titles/types/asset';
import { Source } from '../../queries/ats/titles/types/sources';
import { invariant } from '../../utils/commonUtils';
import { AvailableContext } from './use-available-contexts';
import { InputContext } from './use-input-context';
import { getPreferredSource } from './utils';

export type FocusedAsset = EpisodeAsset | MovieAsset | ProgramAsset;

export function useFocusedAsset(asset: Asset, availableContext: AvailableContext, inputContext: InputContext) {
  return useMemo(() => {
    return getFocusedAsset(asset, availableContext, inputContext);
  }, [asset, availableContext, inputContext]);
}

function getFocusedAsset(asset: Asset, availableContext: AvailableContext, inputContext: InputContext): FocusedAsset {
  if (asset.type === 'series') {
    return getEpisodeInFocus(asset, availableContext, inputContext);
  }

  const preferredSource = getPreferredSource(asset.sources, availableContext, inputContext);

  return {
    ...asset,
    sources: [preferredSource],
  } as FocusedAsset;
}

function getPreferredEpisode(episodes: EpisodeAsset[], source: Source) {
  return episodes.find((episode) => {
    return episode.sources.find((s) => s.platformTitleId === source.platformTitleId);
  });
}

function getEpisodeInFocus(
  asset: SeriesAsset,
  availableContext: AvailableContext,
  inputContext: InputContext,
): EpisodeAsset {
  const episodes = availableContext.episodes(asset);
  const sources = episodes.flatMap((episode) => availableContext.preferredSource(episode, inputContext));
  const preferredSource = getPreferredSource(sources, availableContext, inputContext);
  const preferredEpisode = getPreferredEpisode(episodes, preferredSource);
  invariant(preferredEpisode !== undefined, 'No preferred episode found');
  return {
    ...preferredEpisode,
    sources: [preferredSource],
  };
}
